import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { mockTeamMembers, whatsappTemplates } from '@/mocks/step6Data';

interface TeamMember {
  id: string;
  name: string;
  phone: string;
  status: 'aktif' | 'pasif' | 'ilerliyor' | 'tamamladi';
  step1Completed: boolean;
  lastContact: string;
  note: string;
  joinDate: string;
}

interface SupabaseMember {
  id: string;
  user_id: string;
  name: string;
  phone: string;
  status: string;
  step1_completed: boolean;
  last_contact: string;
  note: string;
  join_date: string;
  parent_id: string | null;
  sort_order: number;
}

function mapSupabaseToTeamMember(sm: SupabaseMember): TeamMember {
  return {
    id: sm.id,
    name: sm.name,
    phone: sm.phone,
    status: sm.status as TeamMember['status'],
    step1Completed: sm.step1_completed,
    lastContact: sm.last_contact,
    note: sm.note,
    joinDate: sm.join_date,
  };
}

type FilterType = 'hepsi' | 'pasif' | 'step1_eksik' | 'yeni_kayit';

const filterLabels: Record<FilterType, string> = {
  hepsi: 'Hepsi',
  pasif: 'Pasif Olanlar',
  step1_eksik: '1. Adım Eksik',
  yeni_kayit: 'Yeni Kayıt',
};

function buildMessage(template: string, member: TeamMember, totalMembers: number) {
  const eksikKisi = Math.max(0, 4 - totalMembers);
  const tahminiGelir = member.step1Completed ? '1.050' : '450';
  return template
    .replace(/{isim}/g, member.name.split(' ')[0])
    .replace(/{durum}/g, member.status === 'aktif' ? 'Aktif' : 'Pasif')
    .replace(/{eksik_kisi}/g, eksikKisi.toString())
    .replace(/{tahmini_gelir}/g, tahminiGelir);
}

function applyFilter(filter: FilterType, memberList: TeamMember[]) {
  const today = new Date();
  const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
  switch (filter) {
    case 'pasif': return memberList.filter(m => m.status === 'pasif');
    case 'step1_eksik': return memberList.filter(m => !m.step1Completed);
    case 'yeni_kayit': return memberList.filter(m => new Date(m.joinDate) >= threeMonthsAgo);
    default: return memberList;
  }
}

interface Template {
  id: string;
  title: string;
  message: string;
  color: string;
  is_default: boolean;
}

const defaultColors = [
  'text-green-400',
  'text-yellow-400',
  'text-orange-400',
  'text-amber-400',
  'text-rose-400',
  'text-cyan-400',
  'text-violet-400',
  'text-pink-400',
];

function getIconForTemplate(title: string): string {
  const t = title.toLowerCase();
  if (t.includes('aktif')) return 'ri-alarm-warning-line';
  if (t.includes('tebrik')) return 'ri-trophy-line';
  if (t.includes('adım') || t.includes('anlat')) return 'ri-footprint-line';
  if (t.includes('motivasyon')) return 'ri-fire-line';
  if (t.includes('geçiş')) return 'ri-arrow-right-circle-line';
  return 'ri-chat-3-line';
}

export default function WhatsAppAksiyonlari() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [filter, setFilter] = useState<FilterType>('hepsi');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [sent, setSent] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [editColor, setEditColor] = useState('text-green-400');
  const [savedFlash, setSavedFlash] = useState(false);

  // Add new template state
  const [addMode, setAddMode] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const selectedTemplate = templates.find(t => t.id === selectedId);

  // Load templates from Supabase
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        // No auth — use mock
        const mockMapped: Template[] = whatsappTemplates.map(t => ({
          id: t.id,
          title: t.title,
          message: t.message,
          color: t.color,
          is_default: true,
        }));
        setTemplates(mockMapped);
        setSelectedId(mockMapped[0]?.id ?? '');
        setUserId(null);
        return;
      }
      setUserId(session.user.id);

      const { data, error } = await supabase
        .from('step6_whatsapp_templates')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (!data || data.length === 0) {
        // First time — seed default templates
        const defaults = whatsappTemplates.map(t => ({
          user_id: session.user.id,
          title: t.title,
          message: t.message,
          color: t.color,
          is_default: true,
        }));

        const { data: seeded, error: seedErr } = await supabase
          .from('step6_whatsapp_templates')
          .insert(defaults)
          .select('*')
          .order('created_at', { ascending: true });

        if (seedErr) throw seedErr;

        setTemplates(seeded || []);
        setSelectedId(seeded?.[0]?.id ?? '');
      } else {
        setTemplates(data);
        setSelectedId(data[0]?.id ?? '');
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
      // Fallback to mock
      const mockMapped: Template[] = whatsappTemplates.map(t => ({
        id: t.id,
        title: t.title,
        message: t.message,
        color: t.color,
        is_default: true,
      }));
      setTemplates(mockMapped);
      setSelectedId(mockMapped[0]?.id ?? '');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Load team members from Supabase
  useEffect(() => {
    const loadMembers = async () => {
      try {
        setMembersLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          setMembers(mockTeamMembers as TeamMember[]);
          return;
        }

        const { data, error } = await supabase
          .from('step6_team_members')
          .select('*')
          .eq('user_id', session.user.id)
          .order('sort_order', { ascending: true });

        if (error || !data || data.length === 0) {
          setMembers(mockTeamMembers as TeamMember[]);
        } else {
          const mapped = (data as SupabaseMember[]).map(mapSupabaseToTeamMember);
          setMembers(mapped);
        }
      } catch {
        setMembers(mockTeamMembers as TeamMember[]);
      } finally {
        setMembersLoading(false);
      }
    };
    loadMembers();
  }, []);

  // Save a template to Supabase
  const saveTemplateToSupabase = async (tpl: Template) => {
    if (!userId) return;
    try {
      await supabase
        .from('step6_whatsapp_templates')
        .update({ title: tpl.title, message: tpl.message, color: tpl.color })
        .eq('id', tpl.id);
    } catch (err) {
      console.error('Failed to save template:', err);
    }
  };

  const handleSelectTemplate = (tmpl: Template) => {
    if (editMode || addMode) return;
    setSelectedId(tmpl.id);
  };

  const startEdit = () => {
    if (!selectedTemplate) return;
    setEditTitle(selectedTemplate.title);
    setEditMessage(selectedTemplate.message);
    setEditColor(selectedTemplate.color);
    setEditMode(true);
    setAddMode(false);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditTitle('');
    setEditMessage('');
  };

  const saveEdit = () => {
    if (!selectedTemplate) return;
    const updated = templates.map(t =>
      t.id === selectedId
        ? { ...t, title: editTitle.trim() || t.title, message: editMessage, color: editColor }
        : t
    );
    setTemplates(updated);
    if (userId) {
      saveTemplateToSupabase({
        ...selectedTemplate,
        title: editTitle.trim() || selectedTemplate.title,
        message: editMessage,
        color: editColor,
      });
    }
    setEditMode(false);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  const resetToDefault = async () => {
    const original = whatsappTemplates.find(t => t.id === selectedTemplate?.id);
    if (!original || !selectedTemplate) return;
    const updated = templates.map(t =>
      t.id === selectedId
        ? { ...t, title: original.title, message: original.message, color: original.color }
        : t
    );
    setTemplates(updated);
    if (userId) {
      try {
        await supabase
          .from('step6_whatsapp_templates')
          .update({ title: original.title, message: original.message, color: original.color })
          .eq('id', selectedId);
      } catch (err) {
        console.error('Failed to reset template:', err);
      }
    }
    if (editMode) cancelEdit();
  };

  const startAdd = () => {
    setAddMode(true);
    setEditMode(false);
    setEditTitle('');
    setEditMessage('');
    setEditColor('text-green-400');
  };

  const cancelAdd = () => {
    setAddMode(false);
    setEditTitle('');
    setEditMessage('');
  };

  const saveNewTemplate = async () => {
    const title = editTitle.trim();
    const message = editMessage.trim();
    if (!title || !message) return;

    if (!userId) {
      // No auth — add to local state with mock ID
      const newTpl: Template = {
        id: `custom-${Date.now()}`,
        title,
        message,
        color: editColor,
        is_default: false,
      };
      setTemplates(prev => [...prev, newTpl]);
      setSelectedId(newTpl.id);
    } else {
      try {
        const { data, error } = await supabase
          .from('step6_whatsapp_templates')
          .insert({
            user_id: userId,
            title,
            message,
            color: editColor,
            is_default: false,
          })
          .select('*')
          .single();

        if (error) throw error;
        if (data) {
          setTemplates(prev => [...prev, data]);
          setSelectedId(data.id);
        }
      } catch (err) {
        console.error('Failed to add template:', err);
      }
    }

    setAddMode(false);
    setEditTitle('');
    setEditMessage('');
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  const deleteTemplate = async () => {
    if (!deleteConfirmId) return;
    const tpl = templates.find(t => t.id === deleteConfirmId);
    if (!tpl || tpl.is_default) return;

    if (userId) {
      try {
        await supabase
          .from('step6_whatsapp_templates')
          .delete()
          .eq('id', deleteConfirmId);
      } catch (err) {
        console.error('Failed to delete template:', err);
      }
    }

    const updated = templates.filter(t => t.id !== deleteConfirmId);
    setTemplates(updated);
    if (selectedId === deleteConfirmId) {
      setSelectedId(updated[0]?.id ?? '');
    }
    setDeleteConfirmId(null);
  };

  const isModified = (() => {
    if (!selectedTemplate) return false;
    const original = whatsappTemplates.find(t => {
      // Compare by similar title for original match
      const origTitle = t.title.toLowerCase();
      const curTitle = selectedTemplate.title.toLowerCase();
      return origTitle.includes(curTitle.substring(0, 5)) || curTitle.includes(origTitle.substring(0, 5));
    });
    if (!original) return selectedTemplate.is_default === false;
    return (
      selectedTemplate.message !== original.message ||
      selectedTemplate.title !== original.title
    );
  })();

  const toggleMember = (id: string) => {
    setSelectedMembers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const filteredMembers = applyFilter(filter, members);

  const selectAll = () => setSelectedMembers(filteredMembers.map(m => m.id));

  const handleSend = (memberId: string) => {
    const member = members.find(m => m.id === memberId);
    if (!member || !selectedTemplate) return;
    const msg = buildMessage(selectedTemplate.message, member, members.length);
    const phone = member.phone.replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    setSent(prev => [...prev, memberId]);
  };

  const handleBulkSend = () => {
    const targets = selectedMembers.length > 0 ? selectedMembers : filteredMembers.map(m => m.id);
    const first = members.find(m => m.id === targets[0]);
    if (first && selectedTemplate) {
      const msg = buildMessage(selectedTemplate.message, first, members.length);
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
    }
    setSent(prev => [...prev, ...targets]);
  };

  const variables = ['{isim}', '{durum}', '{eksik_kisi}', '{tahmini_gelir}'];

  const insertVariable = (v: string) => {
    setEditMessage(prev => prev + v);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#F5A623', borderTopColor: 'transparent' }} />
          <span className="text-sm text-gray-500">Şablonlar yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">WhatsApp Aksiyonları</h1>
        <p className="text-sm text-gray-500 mt-1">Ekibinle iletişimi hızlandır, hazır şablonlarla mesaj gönder</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ── Sol Panel: Şablon Listesi + Düzenleyici ── */}
        <div className="lg:col-span-2 space-y-4">

          {/* Şablon Listesi */}
          <div className="rounded-2xl p-5" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-white">Mesaj Şablonları</h2>
              <button
                onClick={addMode ? cancelAdd : startAdd}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg cursor-pointer transition-all whitespace-nowrap font-medium"
                style={addMode
                  ? { background: 'rgba(239,68,68,0.1)', color: '#F87171', border: '1px solid rgba(239,68,68,0.2)' }
                  : { background: 'rgba(37,211,102,0.1)', color: '#4ADE80', border: '1px solid rgba(37,211,102,0.2)' }}>
                {addMode ? (
                  <>
                    <i className="ri-close-line text-sm" /> İptal
                  </>
                ) : (
                  <>
                    <i className="ri-add-line text-sm" /> Yeni Şablon
                  </>
                )}
              </button>
            </div>

            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {templates.map(tmpl => {
                const orig = whatsappTemplates.find(t => {
                  const origTitle = t.title.toLowerCase();
                  const curTitle = tmpl.title.toLowerCase();
                  return origTitle.includes(curTitle.substring(0, 5)) || curTitle.includes(origTitle.substring(0, 5));
                });
                const changed = orig && (tmpl.message !== orig.message || tmpl.title !== orig.title);
                const isCustom = !orig && !tmpl.is_default;
                return (
                  <button key={tmpl.id} onClick={() => handleSelectTemplate(tmpl)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all text-left group"
                    style={selectedId === tmpl.id
                      ? { background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.2)' }
                      : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <i className={`${getIconForTemplate(tmpl.title)} text-sm ${tmpl.color}`} />
                    </div>
                    <span className={`text-sm font-medium flex-1 truncate ${selectedId === tmpl.id ? 'text-white' : 'text-gray-400'}`}>
                      {tmpl.title}
                    </span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {isCustom && (
                        <span className="text-xs px-1.5 py-0.5 rounded-md font-medium text-emerald-400"
                          style={{ background: 'rgba(52,211,153,0.12)' }}>
                          Özel
                        </span>
                      )}
                      {changed && (
                        <span className="text-xs px-1.5 py-0.5 rounded-md font-medium text-amber-400"
                          style={{ background: 'rgba(245,166,35,0.12)' }}>
                          Düzenlendi
                        </span>
                      )}
                      {selectedId === tmpl.id && <i className="ri-check-line text-green-400 text-sm" />}
                      {!tmpl.is_default && isCustom && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(tmpl.id); }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-red-500/10 cursor-pointer"
                          title="Şablonu sil">
                          <i className="ri-delete-bin-line text-xs text-red-400" />
                        </button>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <p className="text-xs text-gray-600 mt-3">
              {templates.length} şablon — {templates.filter(t => !t.is_default || whatsappTemplates.some(w => w.title.toLowerCase().includes(t.title.toLowerCase().substring(0, 5)))).length} standart, {templates.filter(t => t.is_default === false && !whatsappTemplates.some(w => w.title.toLowerCase().includes(t.title.toLowerCase().substring(0, 5)))).length} özel
            </p>
          </div>

          {/* ── Yeni Şablon Ekleme Formu ── */}
          {addMode && (
            <div className="rounded-2xl p-5" style={{ background: '#12141E', border: '1px solid rgba(37,211,102,0.25)' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(37,211,102,0.12)' }}>
                  <i className="ri-add-circle-line text-sm text-green-400" />
                </div>
                <span className="text-sm font-bold text-white">Yeni Şablon Oluştur</span>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block">Şablon Adı</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    maxLength={50}
                    className="w-full rounded-xl px-3 py-2.5 text-sm text-white outline-none transition-all"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(37,211,102,0.2)' }}
                    placeholder="Örn: Hatırlatma..."
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1.5 block">Mesaj İçeriği</label>
                  <textarea
                    value={editMessage}
                    onChange={e => setEditMessage(e.target.value)}
                    rows={5}
                    className="w-full rounded-xl px-3 py-2.5 text-sm text-gray-200 leading-relaxed outline-none resize-none transition-all"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(37,211,102,0.2)' }}
                    placeholder="Mesaj içeriğini buraya yaz... {isim} gibi değişkenler kullanabilirsin."
                  />
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-2">Değişken Ekle</p>
                  <div className="flex flex-wrap gap-1.5">
                    {variables.map(v => (
                      <button key={v} onClick={() => insertVariable(v)}
                        className="text-xs px-2 py-1 rounded-md font-mono cursor-pointer transition-all text-amber-400 hover:text-amber-300"
                        style={{ background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.2)' }}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-2">Simge Rengi</p>
                  <div className="flex flex-wrap gap-2">
                    {defaultColors.map(c => (
                      <button key={c} onClick={() => setEditColor(c)}
                        className={`w-8 h-8 rounded-lg cursor-pointer transition-all ${c} flex items-center justify-center`}
                        style={editColor === c
                          ? { background: 'rgba(255,255,255,0.1)', border: '2px solid white' }
                          : { background: 'rgba(255,255,255,0.04)', border: '2px solid transparent' }}>
                        <i className={`${getIconForTemplate(editTitle)} text-sm`} />
                      </button>
                    ))}
                  </div>
                </div>

                <button onClick={saveNewTemplate}
                  disabled={!editTitle.trim() || !editMessage.trim()}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-bold text-white cursor-pointer transition-all hover:opacity-90 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
                  <i className="ri-add-line text-base" />
                  Şablonu Kaydet
                </button>
              </div>
            </div>
          )}

          {/* ── Şablon Editörü (mevcut şablonları düzenleme) ── */}
          {!addMode && selectedTemplate && (
            <div className="rounded-2xl p-5" style={{ background: '#12141E', border: editMode ? '1px solid rgba(245,166,35,0.25)' : '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ background: editMode ? 'rgba(245,166,35,0.12)' : 'rgba(37,211,102,0.08)' }}>
                    <i className={`text-sm ${editMode ? 'ri-edit-2-line text-amber-400' : 'ri-whatsapp-line text-green-400'}`} />
                  </div>
                  <span className="text-sm font-bold text-white">
                    {editMode ? 'Şablonu Düzenle' : 'Önizleme'}
                  </span>
                  {savedFlash && (
                    <span className="flex items-center gap-1 text-xs text-green-400 font-medium">
                      <i className="ri-check-line" /> Kaydedildi
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isModified && !editMode && (
                    <button onClick={resetToDefault}
                      className="text-xs px-2.5 py-1.5 rounded-lg cursor-pointer transition-all whitespace-nowrap text-gray-400 hover:text-white"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <i className="ri-refresh-line mr-1" />
                      Sıfırla
                    </button>
                  )}
                  {!editMode && (
                    <button onClick={startEdit}
                      className="text-xs px-2.5 py-1.5 rounded-lg cursor-pointer transition-all whitespace-nowrap text-amber-400 hover:text-amber-300 font-medium"
                      style={{ background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.2)' }}>
                      <i className="ri-edit-2-line mr-1" />
                      Düzenle
                    </button>
                  )}
                </div>
              </div>

              {editMode ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1.5 block">Şablon Adı</label>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={e => setEditTitle(e.target.value)}
                      maxLength={50}
                      className="w-full rounded-xl px-3 py-2.5 text-sm text-white outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(245,166,35,0.2)' }}
                      placeholder="Şablon adı..."
                    />
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 mb-1.5 block">Mesaj İçeriği</label>
                    <textarea
                      value={editMessage}
                      onChange={e => setEditMessage(e.target.value)}
                      rows={7}
                      className="w-full rounded-xl px-3 py-2.5 text-sm text-gray-200 leading-relaxed outline-none resize-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(245,166,35,0.2)' }}
                      placeholder="Mesaj içeriğini buraya yaz..."
                    />
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-2">Simge Rengi</p>
                    <div className="flex flex-wrap gap-2">
                      {defaultColors.map(c => (
                        <button key={c} onClick={() => setEditColor(c)}
                          className={`w-8 h-8 rounded-lg cursor-pointer transition-all ${c} flex items-center justify-center`}
                          style={editColor === c
                            ? { background: 'rgba(255,255,255,0.1)', border: '2px solid white' }
                            : { background: 'rgba(255,255,255,0.04)', border: '2px solid transparent' }}>
                          <i className={`${getIconForTemplate(editTitle)} text-sm`} />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-2">Değişken Ekle</p>
                    <div className="flex flex-wrap gap-1.5">
                      {variables.map(v => (
                        <button key={v} onClick={() => insertVariable(v)}
                          className="text-xs px-2 py-1 rounded-md font-mono cursor-pointer transition-all text-amber-400 hover:text-amber-300"
                          style={{ background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.2)' }}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2 pt-1">
                    <button onClick={saveEdit}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-bold text-white cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
                      style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
                      <i className="ri-save-2-line text-base" />
                      Kaydet
                    </button>
                    <button onClick={cancelEdit}
                      className="px-4 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-all whitespace-nowrap text-gray-400 hover:text-white"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Vazgeç
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl p-4" style={{ background: 'rgba(37,211,102,0.04)', border: '1px solid rgba(37,211,102,0.12)' }}>
                  <p className="text-xs text-gray-300 leading-relaxed whitespace-pre-wrap">{selectedTemplate.message}</p>
                  <div className="mt-3 pt-3 flex flex-wrap gap-1" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <span className="text-xs text-gray-600 mr-1">Değişkenler:</span>
                    {variables.map(v => (
                      <span key={v} className="text-xs px-1.5 py-0.5 rounded font-mono text-amber-400"
                        style={{ background: 'rgba(245,166,35,0.08)' }}>{v}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Sağ Panel: Alıcılar ── */}
        <div className="lg:col-span-3 rounded-2xl p-5" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-sm font-bold text-white">Alıcılar</h2>
            <button onClick={selectAll}
              className="text-xs px-3 py-1.5 rounded-lg cursor-pointer text-amber-400 hover:bg-amber-400/10 transition-colors whitespace-nowrap"
              style={{ border: '1px solid rgba(245,166,35,0.2)' }}>
              Tümünü Seç
            </button>
          </div>

          {/* Filtre */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {(Object.keys(filterLabels) as FilterType[]).map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
                style={filter === f
                  ? { background: 'rgba(245,166,35,0.15)', color: '#F5A623', border: '1px solid rgba(245,166,35,0.25)' }
                  : { background: 'rgba(255,255,255,0.04)', color: '#6B7280', border: '1px solid rgba(255,255,255,0.06)' }}>
                {filterLabels[f]} ({applyFilter(f, members).length})
              </button>
            ))}
          </div>

          <div className="space-y-2 mb-4">
            {membersLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#25D366', borderTopColor: 'transparent' }} />
                  <span className="text-xs text-gray-500">Ekip bilgileri yükleniyor...</span>
                </div>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-2">
                  <i className="ri-user-search-line text-2xl text-gray-600" />
                  <span className="text-xs text-gray-500">Bu filtrede ekip üyesi bulunamadı</span>
                </div>
              </div>
            ) : (
              filteredMembers.map(member => (
              <div key={member.id} className="flex items-center gap-3 p-3 rounded-xl transition-all"
                style={selectedMembers.includes(member.id)
                  ? { background: 'rgba(37,211,102,0.06)', border: '1px solid rgba(37,211,102,0.15)' }
                  : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <input type="checkbox" checked={selectedMembers.includes(member.id)}
                  onChange={() => toggleMember(member.id)}
                  className="w-4 h-4 rounded accent-green-500 cursor-pointer" />
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
                  {member.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{member.name}</div>
                  <div className="text-xs text-gray-500">{member.phone}</div>
                </div>
                <div className="flex items-center gap-2">
                  {sent.includes(member.id) && <i className="ri-check-double-line text-green-400 text-sm" />}
                  <button onClick={() => handleSend(member.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
                    style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', color: 'white' }}>
                    <i className="ri-whatsapp-line text-sm" />
                    Gönder
                  </button>
                </div>
              </div>
            )))}
          </div>

          <button onClick={handleBulkSend}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white cursor-pointer transition-all hover:opacity-90 whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
            <i className="ri-whatsapp-line text-base" />
            {selectedMembers.length > 0 ? `Seçili ${selectedMembers.length} Kişiye` : `${filteredMembers.length} Kişiye`} Toplu Mesaj Gönder
          </button>
        </div>
      </div>

      {/* ── Silme Onay Modalı ── */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="rounded-2xl p-6 w-full max-w-sm mx-4" style={{ background: '#1A1D2A', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.12)' }}>
                <i className="ri-delete-bin-line text-lg text-red-400" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Şablonu Sil</h3>
                <p className="text-xs text-gray-500">Bu işlem geri alınamaz</p>
              </div>
            </div>
            <p className="text-sm text-gray-300 mb-4">
              "{templates.find(t => t.id === deleteConfirmId)?.title}" şablonunu silmek istediğine emin misin?
            </p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium cursor-pointer text-gray-400 hover:text-white transition-all whitespace-nowrap"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                Vazgeç
              </button>
              <button onClick={deleteTemplate}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-all whitespace-nowrap"
                style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}>
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}